import React, { FC, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Row, Typography, Switch, Tooltip, Select, Space } from 'antd'
import { DiffOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useNewIntegrationsStore } from '@/store/newIntegration'
import { useEdgesStore } from '@/store/edges'
import { updateConnector } from '@/shared/api'
import { getAuthToken } from '@/utils/auth'
import {
  DEFAULT_CHANGE_DETECTION_EXCLUDE,
  suggestIdentityPaths,
  type ChangeDetectionConfig,
} from '@/utils/changeDetection'

const { Text } = Typography

const ChangeDetectionIndicator: FC = () => {
  const { id } = useParams()
  const newIntegration = useNewIntegrationsStore(state => state.newIntegration)
  const setNewIntegration = useNewIntegrationsStore(state => state.setNewIntegration)
  const edges = useEdgesStore(state => state.edges)

  const config: ChangeDetectionConfig = newIntegration?.changeDetection || {}
  const enabled = !!config.enabled
  const persistSequence = config.persistSequence !== false

  const targetPaths = useMemo(
    () => [...new Set((edges || []).map(edge => edge.targetPath).filter(Boolean))] as string[],
    [edges]
  )

  const identityOptions = useMemo(() => {
    const suggested = suggestIdentityPaths(targetPaths)
    const extra = (config.identityPaths || []).filter(path => !targetPaths.includes(path))
    return [...new Set([...suggested, ...targetPaths, ...extra])].map(path => ({
      value: path,
      label: path,
    }))
  }, [targetPaths, config.identityPaths])

  const patchConfig = (next: ChangeDetectionConfig) => {
    if (!newIntegration) return
    setNewIntegration({
      ...newIntegration,
      changeDetection: next,
    })
    const integrationId = id || newIntegration._id || newIntegration.connectorId
    const token = getAuthToken()
    if (!integrationId || !token) return
    updateConnector({ id: integrationId, changeDetection: next }, token).catch(error => {
      console.warn('Failed to save change detection:', error)
    })
  }

  const handleToggle = (checked: boolean) => {
    if (!checked) {
      patchConfig({
        ...config,
        enabled: false,
      })
      return
    }
    const identityPaths =
      config.identityPaths?.length ? config.identityPaths : suggestIdentityPaths(targetPaths)
    patchConfig({
      enabled: true,
      identityPaths,
      compare: 'mapped-body',
      excludePaths: config.excludePaths?.length ? config.excludePaths : DEFAULT_CHANGE_DETECTION_EXCLUDE,
      persistSequence: config.persistSequence !== false,
    })
  }

  return (
    <Row
      style={{
        padding: '8px 16px',
        backgroundColor: enabled ? '#f6ffed' : '#fafafa',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 0,
        border: enabled ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
      }}
    >
      <DiffOutlined style={{ color: enabled ? '#389e0d' : '#8c8c8c' }} />
      <Text style={{ marginRight: 4 }}>Skip unchanged records</Text>
      <Tooltip title="Runtime compares the mapped body (minus envelope fields like message_id and sequence) for each identity. Unchanged records are not sent. Same identity with a new body is an update.">
        <InfoCircleOutlined style={{ color: '#1664ff', marginRight: 8 }} />
      </Tooltip>
      <Switch checked={enabled} onChange={handleToggle} size="small" />
      {enabled && (
        <Space wrap size={8} style={{ marginLeft: 8, flex: 1 }}>
          <Text type="secondary">Identity</Text>
          <Select
            mode="tags"
            size="small"
            placeholder="Select identity fields"
            value={config.identityPaths || []}
            options={identityOptions}
            onChange={(identityPaths: string[]) => patchConfig({ ...config, enabled: true, identityPaths })}
            style={{ minWidth: 260, maxWidth: 520 }}
            tokenSeparators={[',']}
          />
          <Text type="secondary">Persist sequence</Text>
          <Switch
            checked={persistSequence}
            size="small"
            onChange={checked => patchConfig({ ...config, enabled: true, persistSequence: checked })}
          />
        </Space>
      )}
    </Row>
  )
}

export default ChangeDetectionIndicator
